'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import CustomWidgetPropTypes from 'DesignData/propTypes/customWidgets';
import { connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import text from 'I18n/utils/unescapedText';
import { EERIE } from 'HubStyleTokens/colors';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILink from 'UIComponents/link/UILink';
import Small from 'UIComponents/elements/Small';
import UITable from 'UIComponents/table/UITable';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UISearchInput from 'UIComponents/input/UISearchInput';
import UIFlex from 'UIComponents/layout/UIFlex';
import CustomWidgetField from 'DesignData/models/CustomWidgetField';
import SharedDragDropContext from 'DesignData/lib/SharedDragDropContext';
import * as customWidgetEditorOptionsSelectors from 'DesignData/selectors/customWidgetEditorOptions';
import { findMatchingFields } from 'DesignData/helpers/customWidgetFieldsHelper';
import { EDIT, CREATE } from 'DesignData/enums/CustomWidgetFieldTypePermissions';
import FieldsTableRow from 'DesignEditorsUI/components/customWidgetEditor/sidebar/main/fieldsTable/FieldsTableRow';
import { compose } from 'redux';

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.main.fields.${key}`;

function makeMapStateToProps() {
  const getFieldTypePermissions = customWidgetEditorOptionsSelectors.makeGetFieldTypePermissions();
  return (state, props) => {
    return {
      fieldGrouping: customWidgetEditorOptionsSelectors.getFieldGrouping(state, props),
      fieldTypePermissions: getFieldTypePermissions(state, props)
    };
  };
}

const mapDispatchToProps = {};

class FieldsTable extends PureComponent {
  constructor(props) {
    super(props);

    this.onSearchInputChange = e => {
      this.setState({
        searchTerm: e.target.value
      });
    };

    this.onSearchInputClear = () => {
      this.setState({
        searchTerm: ''
      });
    };

    this.renderField = field => {
      const {
        customWidgetId,
        fieldGrouping,
        fieldName,
        fieldTypePermissions,
        isReadOnly,
        isStyleTab,
        depth
      } = this.props;
      const fieldNameProp = fieldName ? {
        fieldName: `${fieldName}.${field.name}`
      } : {};

      const _fieldTypePermissions = fieldTypePermissions[field.type] || {};

      const canEditFieldType = !!_fieldTypePermissions[EDIT];
      const canCreateFieldType = !!_fieldTypePermissions[CREATE];
      return /*#__PURE__*/_jsx("tr", {
        className: "custom-widget-fields-table-row",
        children: /*#__PURE__*/_jsx("td", {
          className: "p-all-0",
          children: /*#__PURE__*/_createElement(FieldsTableRow, Object.assign({}, fieldNameProp, {
            canEditFieldType: canEditFieldType,
            canCreateFieldType: canCreateFieldType,
            customWidgetId: customWidgetId,
            field: field,
            fieldGrouping: fieldGrouping,
            isReadOnly: isReadOnly,
            isStyleTab: isStyleTab,
            isDraggable: !this.state.searchTerm,
            key: `fields-table-row-${field.id}`,
            depth: depth
          }))
        })
      }, field.id);
    };

    this.state = {
      searchTerm: ''
    };
  }

  getMatchingFields() {
    const {
      searchTerm
    } = this.state;
    const {
      fields,
      fieldGrouping: {
        isActive
      }
    } = this.props;
    return !isActive && searchTerm ? findMatchingFields(fields, searchTerm) : fields;
  }

  renderFields() {
    return this.getMatchingFields().map(this.renderField);
  }

  renderSearch() {
    const {
      searchTerm
    } = this.state;
    const {
      fieldGrouping: {
        isActive
      },
      depth
    } = this.props;

    if (isActive || depth > 0) {
      return null;
    }

    let searchResults = null;

    if (searchTerm) {
      const matchingFields = this.getMatchingFields();
      const resultsI18nKey = matchingFields.length ? 'results' : 'noResults';
      searchResults = /*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        className: "fields-table__search__results",
        justify: "between",
        children: [/*#__PURE__*/_jsx(Small, {
          className: "fields-results-text",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey(`search.${resultsI18nKey}`),
            options: {
              searchTerm,
              count: matchingFields.length
            }
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          onClick: this.onSearchInputClear,
          use: "unstyled",
          children: /*#__PURE__*/_jsx(UIIcon, {
            color: EERIE,
            name: "remove"
          })
        })]
      });
    }

    return /*#__PURE__*/_jsxs("div", {
      className: "fields-table__search",
      children: [/*#__PURE__*/_jsx(UISearchInput, {
        onChange: this.onSearchInputChange,
        placeholder: text(i18nKey('search.placeholder')),
        value: searchTerm
      }), searchResults]
    });
  }

  renderStyleTabOptIn() {
    const {
      createStyleFieldGroup,
      isReadOnly
    } = this.props;
    return /*#__PURE__*/_jsxs("div", {
      className: "opt-in",
      children: [/*#__PURE__*/_jsx(UIHelpIcon, {
        title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: i18nKey('styleTabOptIn.help_jsx'),
          options: {
            external: true,
            href: 'https://developers.hubspot.com/docs/cms/building-blocks/module-theme-fields-overview#style-fields'
          },
          elements: {
            Link: UILink
          }
        }),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('styleTabOptIn.label')
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        className: "p-top-3",
        use: "primary",
        onClick: createStyleFieldGroup,
        disabled: isReadOnly,
        "data-test-id": "style-tab-opt-in",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('styleTabOptIn.button')
        })
      })]
    });
  }

  render() {
    const {
      isStyleTab,
      styleFields
    } = this.props;

    if (isStyleTab && !styleFields) {
      return /*#__PURE__*/_jsx("div", {
        children: this.renderStyleTabOptIn()
      });
    }

    if (!this.props.fields.length) {
      return /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('noFields')
        })
      });
    }

    return /*#__PURE__*/_jsxs("div", {
      children: [this.renderSearch(), /*#__PURE__*/_jsx(UITable, {
        className: "custom-widget-fields-table",
        flush: true,
        hover: false,
        children: /*#__PURE__*/_jsx("tbody", {
          children: this.renderFields()
        })
      })]
    });
  }

}

FieldsTable.propTypes = {
  // Passed in props
  customWidgetId: CustomWidgetPropTypes.customWidgetId.isRequired,
  fields: PropTypes.arrayOf(PropTypes.instanceOf(CustomWidgetField)).isRequired,
  styleFields: PropTypes.instanceOf(CustomWidgetField),
  isStyleTab: PropTypes.bool,
  styleTab: PropTypes.instanceOf(CustomWidgetField),
  fieldName: PropTypes.string,
  depth: PropTypes.number,
  isReadOnly: PropTypes.bool,
  createStyleFieldGroup: PropTypes.func,
  // @connect
  fieldGrouping: PropTypes.object.isRequired,
  fieldTypePermissions: PropTypes.object.isRequired
};
FieldsTable.defaultProps = {
  depth: 0,
  isReadOnly: false,
  isStyleTab: false
};
export default compose(SharedDragDropContext(), connect(makeMapStateToProps, mapDispatchToProps))(FieldsTable);